import { EditorContent } from '@tiptap/react';
import React, { ReactElement } from 'react';
import { useDispatch } from 'react-redux';

import { ImMNElement } from '../../../../common/elements/elementModelTypes';
import { getElementId, getIgnoreAutoType } from '../../../../common/elements/utils/elementPropertyUtils';
import { useSuggestionsManager } from '../../../components/tiptapEditor/useSuggestionsManager';
import { SearchHighlightObserver } from '../../../../common/tiptap/extensions/SearchHighlighter';
import { TiptapContent } from '../../../../common/tiptap/tiptapTypes';
import usePoiUpdateHandler from '../../../components/pointsOfInterest/usePoiUpdateHandler';
import useClientElementTiptapEditor from '../../../components/tiptapEditor/useClientElementTiptapEditor';
import { DispatchCreateNewElementFunction } from '../../actions/elementActionTypes';
import { getMainEditorId, getMainEditorKey } from '../../utils/elementEditorUtils';
import useCardTiptapEditorExtensions from './useCardTiptapEditorExtensions';
import useTiptapInnerRef from '../../../../common/tiptap/hooks/useTiptapInnerRef';
import { SaveTiptapEditorContentFunction } from '../../../../common/tiptap/extensions/externalStoreSync/externalStoreSynchroniserTypes';

import './CardTiptapEditor.scss';
import { useTranslator } from '../../../components/i18n/useTranslator';

interface CardTiptapEditorProps {
    element: ImMNElement;

    currentEditorId: string;

    isEditable: boolean;
    isEditing: boolean;
    isSingleSelected: boolean;
    isFocusedForegroundElement: boolean;

    textContent: TiptapContent | null;
    saveContent: SaveTiptapEditorContentFunction;
    startEditing: (args: { editorId: string; editorKey: string }) => void;
    editorRef: (elem: HTMLDivElement | null) => void;

    moveElementsToTrash: () => void;
    createNewCard?: DispatchCreateNewElementFunction;
}

const CardTiptapEditor = (props: CardTiptapEditorProps): ReactElement => {
    const {
        element,
        editorRef,
        textContent,
        currentEditorId,
        saveContent,
        isEditable,
        isEditing,
        isSingleSelected,
        startEditing,
        moveElementsToTrash,
        createNewCard,
    } = props;

    const dispatch = useDispatch();
    const editorId = getMainEditorId(props);
    const editorKey = getMainEditorKey(props);
    const elementId = getElementId(element);

    const { innerRef, contentInnerRef } = useTiptapInnerRef(editorRef);

    const onHighlightsChanged = usePoiUpdateHandler({ contentInnerRef, elementId });

    const [suggestionsManager, mentionsPopup] = useSuggestionsManager({ trackInsertions: true });

    const translate = useTranslator('elements', 'elementTypes.card');

    const extensions = useCardTiptapEditorExtensions({
        moveElementsToTrash,
        saveContent,
        suggestionsManager,
        createNewCard,
        isEditable,
        dispatch,
        onHighlightsChanged,
        universalCardConversion: !getIgnoreAutoType(element),
        elementId,
        translate,
        trackInsertions: true,
    });

    const { editor, onMouseDown, onClick } = useClientElementTiptapEditor({
        persistedContent: textContent,
        extensions,

        editorId,
        editorKey,
        currentEditorId,

        isEditable,
        isEditing,
        isSingleSelected,

        startEditing,
        saveContent,
    });

    // NOTE: onMouseDown & onClick needed to be added to the containing div for them to work with
    //  the React version of addNodeViews in Tiptap extensions, otherwise click events etc that
    //  occur within the node views wouldn't trigger those methods
    return (
        <div className="TiptapEditorContainer" onMouseDown={onMouseDown} onClick={onClick}>
            {mentionsPopup}
            <SearchHighlightObserver editor={editor} />
            <EditorContent innerRef={innerRef} className="CardTiptapEditor" dir="auto" editor={editor} />
        </div>
    );
};

export default CardTiptapEditor;
